import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Scorecard from './Scorecard';
import BetweenRounds from './BetweenRounds';
import CustomModal from './CustomModal';

function EnterScores() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [currentCourseIndex, setCurrentCourseIndex] = useState(0);
  const [holes, setHoles] = useState([]);
  const [showBetweenRounds, setShowBetweenRounds] = useState(false);
  const [currentRoundData, setCurrentRoundData] = useState(null);
  const [eventStats, setEventStats] = useState({ totalShots: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '', onConfirm: null });

  useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

  useEffect(() => {
    fetch(`/server/crazy-cup-server/getEventDetails.php?id=${eventId}`)
      .then((response) => response.json())
      .then((data) => setEvent(data))
      .catch((error) => console.error('Error fetching event details:', error));
  }, [eventId]);

  useEffect(() => {
    if (event) {
      const currentCourse = event.courses[currentCourseIndex];
      if (currentCourse && currentCourse.id) {
        // Check if scores already exist for this course/event/user
        const clerkId = localStorage.getItem('userId');
        fetch(`/server/crazy-cup-server/checkScoresExist.php?eventId=${event.id}&courseId=${currentCourse.id}&userId=${clerkId}`)
          .then((response) => response.json())
          .then((data) => {
            if (data.scoresExist) {
              setShowBetweenRounds(false);
              setCurrentRoundData(null);
              setModalContent({
                title: 'Scores Already Entered',
                message: 'Scores already exist for this course. You will be redirected to the next course.',
                onConfirm: () => {
                  // Find the next course without saved scores
                  let nextIndex = currentCourseIndex + 1;
                  const goToNext = () => {
                    if (nextIndex >= event.courses.length) return;
                    fetch(`/server/crazy-cup-server/checkScoresExist.php?eventId=${event.id}&courseId=${event.courses[nextIndex].id}&userId=${clerkId}`)
                      .then((r) => r.json())
                      .then((d) => {
                        if (!d.scoresExist) {
                          setCurrentCourseIndex(nextIndex);
                          setModalOpen(false);
                        } else {
                          nextIndex++;
                          goToNext();
                        }
                      });
                  };
                  goToNext();
                }
              });
              setModalOpen(true);
            } else {
              fetch(`/server/crazy-cup-server/getHoles.php?course_id=${currentCourse.id}`)
                .then((response) => response.json())
                .then((data) => setHoles(data))
                .catch((error) => console.error('Error fetching holes:', error));
            }
          })
          .catch((error) => console.error('Error checking for existing scores:', error));
      } else {
        console.error('Current course is missing or does not have an ID.');
      }
    }
  }, [event, currentCourseIndex]);

  const handleSaveScores = (scores) => {
    const currentCourse = event.courses[currentCourseIndex];
    const totalShots = scores.reduce((sum, item) => sum + item.shots, 0);
    const holesInOne = scores.filter((item) => item.shots === 1).length;
    const overUnderPar = scores.reduce((sum, item) => sum + (item.shots - item.par), 0);
    const holesPlayed = scores.length;
    const clerkId = localStorage.getItem('userId');
    const payload = {
      userId: clerkId,
      eventId: event.id,
      courseId: currentCourse.id,
      scores: JSON.stringify(scores),
      totalShots,
      holesInOne,
      overUnderPar,
      holesPlayed,
    };
    return fetch('/server/crazy-cup-server/saveScores.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(() => {
        setEventStats((prevStats) => ({
          totalShots: prevStats.totalShots + totalShots,
        }));
        setCurrentRoundData({ totalShots, holesInOne, overUnderPar, holesPlayed, scores });
        if (holesPlayed < currentCourse.holes.length) {
          return;
        }
        setShowBetweenRounds(true);
      })
      .catch((error) => console.error('Error saving scores:', error));
  };

  const handleCompleteEvent = () => {
    const clerkId = localStorage.getItem('userId');
    fetch('/server/crazy-cup-server/markEventCompleted.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clerkUserId: clerkId, eventId }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setShowBetweenRounds(true);
        } else {
          alert('Error marking event as completed: ' + data.message);
        }
      })
      .catch((error) => console.error('Error marking event as completed:', error));
  };

  if (!event) return <div id="loading-event" className="loading">Loading event details...</div>;

  if (showBetweenRounds && currentRoundData) {
    const nextCourse = event.courses[currentCourseIndex + 1];
    return (
      <BetweenRounds
        type={currentCourseIndex === event.courses.length - 1 ? 'end-event' : 'between-rounds'}
        currentRoundData={currentRoundData} 
        eventStats={eventStats}
        nextCourse={nextCourse}
        scores={currentRoundData.scores}
        eventId={event.id}
        onStartNextRound={() => {
          setShowBetweenRounds(false);
          setCurrentCourseIndex(currentCourseIndex + 1);
        }}
      />
    );
  }

  return (
    <div id="enter-scores-page" className="page">
      <h1 className='page-title' id="event-title">{event.cup_name.toUpperCase()}</h1>
      <h2 id="round-title">
        {currentCourseIndex === event.courses.length - 1 ? 'FINAL ROUND' : `Round ${currentCourseIndex + 1}`}
      </h2>
      <Scorecard
        course={event.courses[currentCourseIndex]}
        holes={holes}
        onSave={(scores, isCourseComplete) => {
          handleSaveScores(scores).then(() => {
            if (isCourseComplete) {
              if (currentCourseIndex === event.courses.length - 1) {
                handleCompleteEvent();
              } else {
                setShowBetweenRounds(true);
              }
            }
          });
        }}
        isLastCourse={currentCourseIndex === event.courses.length - 1}
        eventId={event.id}
        handleCompleteEvent={handleCompleteEvent}
      />
      <CustomModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalContent.title}
        message={modalContent.message}
        onConfirm={modalContent.onConfirm}
      />
    </div>
  );
}

export default EnterScores;